import React, { useState, useCallback } from 'react';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/ToastContext';
import { ReviewData } from './types';

interface ReviewFormProps {
  productId?: string;
  shopId?: string;
  onSubmit: (review: ReviewData) => Promise<void>;
  onCancel?: () => void;
}

const RATING_LABELS = ['Pésimo', 'Malo', 'Regular', 'Bueno', 'Excelente'];

export const ReviewForm: React.FC<ReviewFormProps> = ({ 
  productId, 
  shopId, 
  onSubmit,
  onCancel 
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleImageUpload = useCallback((files: FileList | null) => {
    if (!files) return;
    
    const remainingSlots = 5 - images.length;
    if (remainingSlots <= 0) {
      addToast('Máximo 5 imágenes permitidas', 'warning');
      return;
    }

    const newImages: string[] = [];
    Array.from(files).slice(0, remainingSlots).forEach(file => {
      if (!file.type.startsWith('image/')) {
        addToast(`${file.name} no es una imagen válida`, 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        addToast(`${file.name} es muy grande (max 5MB)`, 'error');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          newImages.push(e.target.result as string);
          if (newImages.length === Math.min(files.length, remainingSlots)) {
            setImages(prev => [...prev, ...newImages]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  }, [images.length, addToast]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleImageUpload(e.dataTransfer.files);
  }, [handleImageUpload]);

  const removeImage = useCallback((index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      addToast('Inicia sesión para dejar una reseña', 'info');
      return;
    }
    
    if (rating === 0) {
      addToast('Selecciona una calificación', 'warning');
      return;
    }
    
    if (comment.trim().length < 10) {
      addToast('El comentario debe tener al menos 10 caracteres', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        productId,
        shopId,
        rating,
        comment: comment.trim(),
        images,
        customerId: user.uid,
        customerName: user.displayName || user.email?.split('@')[0] || 'Usuario',
        customerAvatar: user.photoURL || undefined,
      });
      
      // Reset form
      setRating(0);
      setComment('');
      setImages([]);
      addToast('¡Gracias por tu reseña!', 'success');
    } catch (error) {
      addToast('Error al enviar la reseña', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-white/10 p-5">
      <h4 className="font-bold text-lg mb-4">Escribir una reseña</h4>
      
      {/* Star Rating */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-text-secondary mb-2">
          ¿Qué te pareció? <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="p-1 transition-transform hover:scale-110"
              aria-label={`${star} estrellas`}
            >
              <span className={`material-symbols-outlined text-3xl ${
                star <= (hoverRating || rating) 
                  ? 'filled text-yellow-400' 
                  : 'text-gray-300'
              }`}>
                star
              </span>
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-sm text-primary font-medium mt-1">
            {RATING_LABELS[rating - 1]}
          </p>
        )}
      </div>

      {/* Comment */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Tu experiencia <span className="text-red-500">*</span>
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="¿Qué te gustó? ¿Qué no? ¿Lo recomendarías?"
          rows={4}
          className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-xl bg-gray-50 dark:bg-black/20 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          maxLength={1000}
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">Mínimo 10 caracteres</span>
          <span className={`text-xs ${comment.length < 10 ? 'text-red-400' : 'text-gray-400'}`}>
            {comment.length}/1000
          </span>
        </div>
      </div>

      {/* Photo Upload */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Agregar fotos (opcional)
        </label>
        
        {images.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
            {images.map((image) => (
              <div key={image} className="relative shrink-0">
                <img 
                  src={image} 
                  alt={`Preview ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {images.length < 5 && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              isDragging 
                ? 'border-primary bg-primary/5' 
                : 'border-gray-300 dark:border-white/10 hover:border-primary/50'
            }`}
          >
            <span className="material-symbols-outlined text-3xl text-gray-400 mb-2">
              add_photo_alternate
            </span>
            <p className="text-sm text-text-secondary mb-2">
              Arrastra fotos aquí o{' '}
              <label className="text-primary font-medium cursor-pointer hover:underline">
                selecciona archivos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e.target.files)}
                  className="hidden"
                />
              </label>
            </p>
            <p className="text-xs text-gray-400">
              JPG, PNG o WEBP. Máx 5MB. ({images.length}/5)
            </p>
          </div>
        )}
      </div>

      {/* Submit Buttons */}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 py-3 border border-gray-200 dark:border-white/10 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || rating === 0 || comment.trim().length < 10}
          className="flex-1 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Enviando...
            </span>
          ) : (
            'Publicar reseña'
          )}
        </button>
      </div>
    </form>
  );
};
